import React, { useState, useEffect } from 'react';
import { config } from '../config';
import { useNavigate } from 'react-router-dom';
import { Clock, User, Image as ImageIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import DOMPurify from 'dompurify';
import Header from './Header';

const BlogList = () => {
    const [blogs, setBlogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [limit] = useState(6); // blogs per page
    const navigate = useNavigate();

    useEffect(() => {
        const fetchBlogs = async () => {
            setLoading(true);
            try {
                const response = await fetch(`${config.API_HOST}/api/user/getBlogs?page=${currentPage}&limit=${limit}`);
                if (!response.ok) throw new Error('Failed to fetch blogs');
                const data = await response.json();
                setBlogs(data.blogs);
                setTotalPages(data.totalPages);
                // console.log("blogs data", data.blogs);
            } catch (error) {
                setError('Unable to load blogs. Please try again later.');
            } finally {
                setLoading(false);
            }
        };

        fetchBlogs();
    }, [currentPage, limit]);

    const createExcerpt = (content) => {
        // Return early if no content is available
        if (!content) return 'No content available...';
        
        try {
            if (typeof content === 'string') {
                // Check if the content contains raw HTML tags as strings
                if (content.includes('<h2 style=') || content.includes('<strong style=')) {
                    // Clean up raw HTML tags that are showing as text
                    let cleanedContent = content.replace(/<([^>]+)>/g, '');
                    cleanedContent = cleanedContent.trim();
                    return cleanedContent.length > 150 ? cleanedContent.slice(0, 150) + '...' : cleanedContent;
                }
                
                // Handle proper HTML content
                const div = document.createElement('div');
                div.innerHTML = DOMPurify.sanitize(content);
                
                // Extract text content from the sanitized HTML
                let textContent = div.textContent || div.innerText || '';
                
                // Trim whitespace and return excerpt
                textContent = textContent.trim();
                let cleanedContent = textContent.replace(/<([^>]+)>/g, '');
                cleanedContent = cleanedContent.trim();
                // console.log("textContent",cleanedContent)
                return textContent.length > 150 ? cleanedContent.slice(0, 150) + '...' : cleanedContent;
            }
            
            // Handle plain text content
            return String(content).slice(0, 150) + '...';
        } catch (error) {
            console.error('Error creating excerpt:', error);
            return 'No content available...';
        }
    };

    const DefaultBlogImage = () => (
        <div className="w-full h-48 bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center">
            <ImageIcon className="w-12 h-12 text-white opacity-80" />
        </div>
    );

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setCurrentPage(newPage);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const renderPagination = () => {
        if (totalPages <= 1) return null;

        const pageNumbers = [];
        const maxVisiblePages = 5;
        
        let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
        let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
        
        if (endPage - startPage + 1 < maxVisiblePages) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }

        for (let i = startPage; i <= endPage; i++) {
            pageNumbers.push(i);
        }

        return (
            <div className="flex justify-center items-center space-x-2 mt-12">
                {/* Previous button */}
                <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="flex items-center px-3 py-2 rounded-lg bg-white shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                >
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    Previous
                </button>

                {/* Page numbers */}
                <div className="flex space-x-1">
                    {startPage > 1 && (
                        <>
                            <button
                                onClick={() => handlePageChange(1)}
                                className="px-3 py-2 rounded-lg bg-white shadow-md hover:shadow-lg hover:bg-blue-50 transition-all duration-200"
                            >
                                1
                            </button>
                            {startPage > 2 && (
                                <span className="px-3 py-2 text-gray-500">...</span>
                            )}
                        </>
                    )}

                    {pageNumbers.map(pageNum => (
                        <button
                            key={pageNum}
                            onClick={() => handlePageChange(pageNum)}
                            className={`px-3 py-2 rounded-lg shadow-md transition-all duration-200 ${
                                currentPage === pageNum
                                    ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white'
                                    : 'bg-white hover:shadow-lg hover:bg-blue-50'
                            }`}
                        >
                            {pageNum}
                        </button>
                    ))}

                    {endPage < totalPages && (
                        <>
                            {endPage < totalPages - 1 && (
                                <span className="px-3 py-2 text-gray-500">...</span>
                            )}
                            <button
                                onClick={() => handlePageChange(totalPages)}
                                className="px-3 py-2 rounded-lg bg-white shadow-md hover:shadow-lg hover:bg-blue-50 transition-all duration-200"
                            >
                                {totalPages}
                            </button>
                        </>
                    )}
                </div>

                {/* Next button */}
                <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="flex items-center px-3 py-2 rounded-lg bg-white shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                >
                    Next
                    <ChevronRight className="w-4 h-4 ml-1" />
                </button>
            </div>
        );
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center min-h-screen bg-gradient-to-br from-blue-50 to-purple-50">
                <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-purple-600 shadow-lg" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="text-center text-red-600 bg-red-50 p-6 rounded-lg shadow-md mt-10 max-w-md mx-auto">
                <p className="font-semibold">{error}</p>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-purple-50 py-16 px-4 sm:px-6 lg:px-8">
            <div className="fixed top-0 left-0 w-full z-50">
                <Header/>
            </div>
            <div className="max-w-7xl mx-auto mt-10">
                <div className="text-center mb-12">
                    <h1 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 mb-3">
                        Explore Our Blogs
                    </h1>
                    <p className="text-gray-600 text-lg max-w-2xl mx-auto">
                        Discover interesting stories, insights, and perspectives from our community
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {blogs.map(blog => (
                        <div 
                            key={blog._id}
                            className="group bg-white rounded-2xl shadow-md overflow-hidden transition-all duration-300 hover:shadow-xl hover:-translate-y-2"
                        >
                            <div className="relative h-48 overflow-hidden">
                                {blog.image ? (
                                    <img 
                                        src={blog.image} 
                                        loading="lazy" 
                                        alt={blog.title || 'Blog post'} 
                                        className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-300"
                                    />
                                ) : (
                                    <DefaultBlogImage />
                                )}
                            </div>
                            
                            <div className="p-6">
                                <h2 className="text-xl font-bold text-gray-800 mb-3 group-hover:text-blue-600 transition-colors line-clamp-2">
                                    {blog.title || 'Untitled Post'}
                                </h2>
                                <p className="text-gray-600 mb-4 text-sm line-clamp-3">
                                    {createExcerpt(blog.content)}
                                </p>
                                
                                <div className="flex items-center text-sm text-gray-500 space-x-4 mb-6">
                                    <div className="flex items-center space-x-2">
                                        <User className="h-4 w-4 text-blue-500" />
                                        <span>{blog.author || 'Anonymous'}</span>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <Clock className="h-4 w-4 text-purple-500" />
                                        <span>{blog.createdAt ? new Date(blog.createdAt).toLocaleDateString() : 'Unknown date'}</span>
                                    </div>
                                </div>

                                <button 
                                    onClick={() => navigate(`/blog/${blog._id}`)}
                                    className="w-full py-2.5 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-xl hover:from-blue-600 hover:to-purple-600 transition-all duration-300 font-medium"
                                >
                                    Read More →
                                </button>
                            </div>
                        </div>
                    ))}
                </div>

                {blogs.length === 0 && (
                    <div className="text-center text-gray-500 py-16 bg-white rounded-2xl shadow-md">
                        <ImageIcon className="w-16 h-16 mx-auto text-gray-400 mb-4" />
                        <p className="text-xl font-medium">No blogs available at the moment.</p>
                    </div>
                )}

                {/* Pagination */}
                {renderPagination()}
            </div>
        </div>
    );
};

export default BlogList;